import { useCallback, useMemo, useState } from 'react';

import type { CountryListProps } from '@app/components/Phone/types';
import { CountryAlpha2 } from '@app/types';

import type { ChangeEvent } from 'react';

export const useCountryList = ({
  countries,
  isOpen,
  onSelect,
}: CountryListProps) => {
  const [searchValue, setSearchValue] = useState('');

  const skipSearch = useCallback(() => {
    setSearchValue('');
  }, []);

  const handleSearchInputChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      const regex = new RegExp('^[1-79]');
      if (regex.test(value)) {
        value = `+${value}`;
      }
      if (value === '+') {
        value = '';
      }

      setSearchValue(value);
    },
    []
  );

  const escapeRegExp = useCallback((input: string): string => {
    const specialCharacters = new RegExp(/[.*+?^${}()|[\]\\]/g);
    return input.replace(specialCharacters, '\\$&');
  }, []);

  const filteredCountries = useMemo(() => {
    const escapedSearchValue = escapeRegExp(searchValue);
    const regex = new RegExp(escapedSearchValue, 'i');

    return countries.filter(({ Name, TelCode, Alpha2 }) => {
      if (Alpha2 === CountryAlpha2.UNKNOWN) {
        return false;
      }
      if (escapedSearchValue === '8') {
        return Name === 'Россия';
      } else {
        return regex.test(Name) || regex.test(`+${TelCode}`);
      }
    });
  }, [searchValue, countries, escapeRegExp]);

  const skipSearchHandler = useCallback(() => {
    if (!isOpen) skipSearch();
  }, [isOpen, skipSearch]);

  const onSelectHandler = useCallback(
    (countryAlpha2: CountryAlpha2) => {
      onSelect(countryAlpha2);
      skipSearch();
    },
    [onSelect, skipSearch]
  );

  return {
    skipSearchHandler,
    handleSearchInputChange,
    filteredCountries,
    searchValue,
    onSelectHandler,
  };
};
